import { Schemas } from "./schemas";
import { Migrations } from "./migrate";
import type { LatestConfig } from "./schemas";
import { BlockDefs, type BlockDefMeta } from "./blocks/defs/blocks";
import type { BlockType } from "./blocks/types";
import type { Region } from "./blocks/defs/regions";

type SchemaVersion = keyof typeof Schemas;

type VersionedInput = {
  version: SchemaVersion;
};

type StripResult = {
  layout: LatestConfig["layout"];
  removed: Array<{ region: Region; index: number; type: BlockType; missing: string[] }>;
};

type Addons = Record<string, unknown>;

function isVersionedInput(x: unknown): x is VersionedInput {
  if (typeof x !== "object" || x === null) return false;

  // Narrow from object -> indexable record
  const r = x as Record<string, unknown>;
  const v = r["version"];

  // Ensure version exists and is a key in Schemas
  return typeof v === "string" && v in Schemas;
}


export function parseAndMigrate(raw: unknown): LatestConfig {
  console.info(`Parsing and migrating configuration of type ${typeof raw}`, raw);
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (err) {
      console.error("Failed to parse JSON:", err);
      throw new Error("Invalid configuration: not valid JSON");
    }
  }

  if (!isVersionedInput(raw)) {
    throw new Error('Invalid configuration: missing or invalid version');
  }

  const version = raw.version;
  const schema = Schemas[version as keyof typeof Schemas];
  if (!schema) throw new Error(`Unsupported config version: ${version}`);

  const parsed = schema.parse(raw); 

  const migrate = Migrations[version as keyof typeof Migrations];

  if (!migrate) throw new Error(`No migration available for version: ${version}`);

  const latest = migrate(parsed);

  const enabled = enabledAddonKeys(latest.addons as Addons);

  const { layout, removed } = stripGatedBlocksFromLayout(latest.layout, enabled, BlockDefs);
  if (removed.length) {
    console.info("Stripped gated blocks", removed);
  }

  return { ...latest, layout };
}

function enabledAddonKeys(addons: Addons | undefined | null): Set<string> {
  if (!addons || typeof addons !== "object") return new Set();
  console.log(JSON.stringify(addons));
  const enabled = new Set<string>();
  for (const [k, ] of Object.entries(addons)) {
    enabled.add(k);
  }
  return enabled;
}

function stripGatedBlocksFromLayout(
  layout: LatestConfig["layout"],
  enabled: Set<string>,
  blockDefs: Record<BlockType, BlockDefMeta>
): StripResult {
  const removed: StripResult["removed"] = [];

  const nextRegions = { ...layout.regions } as typeof layout.regions;

  for (const region of Object.keys(layout.regions) as Region[]) {
    const blocks = layout.regions[region] ?? [];
    const kept: typeof blocks = [];

    blocks.forEach((block, idx) => {
      const meta = blockDefs[block.type as BlockType];
      if (!meta) {
        kept.push(block);
        return;
      }

      const req = meta.requiresFeature ?? [];
      
      const missing = req.filter((f) => !enabled.has(f));
      if (missing.length) {
        removed.push({ region, index: idx, type: block.type, missing });
        return;
      }
      kept.push(block);
    });

    nextRegions[region] = kept;
  }

  return {
    layout: { ...layout, regions: nextRegions },
    removed,
  };
}